import {
  type GetApiAdminCompanyByCompanyTreeSettingsIdResponse,
  usePatchAdminCompany,
} from '@sit/client-shared';
import { type TenantType, TenantTypes } from '@sit/core';
import { useForm } from '@tanstack/react-form';
import { noop } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Card, {
  CardColumn,
  CardFooter,
  CardRow,
} from 'carbon-react/lib/components/card';
import Divider from 'carbon-react/lib/components/divider';
import Form from 'carbon-react/lib/components/form';
import Loader from 'carbon-react/lib/components/loader/__next__';
import { Option, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';
import Typography from 'carbon-react/lib/components/typography';
import toastr from '../../../helpers/toastr';

export interface CompanyDetailsProps {
  company: GetApiAdminCompanyByCompanyTreeSettingsIdResponse;
}

const emptyToNull = <T extends string | null | undefined>(value: T) => {
  return value === '' || value == null ? null : value;
};

const tenantTypeOptions = Object.values(TenantTypes);

const isTenantType = (value: string): value is TenantType => {
  return tenantTypeOptions.some((option) => option === value);
};

export function CompanyDetailsForm({ company }: CompanyDetailsProps) {
  const mutation = usePatchAdminCompany();

  const form = useForm({
    defaultValues: {
      externalId: company.externalId,
      tenantType: company.tenantType || 'customer_production',
      syncError: company.syncError || null,
      customIntacctEndpoint: company.customIntacctEndpoint ?? null,
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(
        {
          body: {
            tenantType: value.tenantType,
            syncError: emptyToNull(value.syncError),
            customIntacctEndpoint: emptyToNull(value.customIntacctEndpoint),
          },
          pathParams: {
            id: `${company.id}`,
          },
        },
        {
          onSuccess: () => {
            toastr.success('Company updated');
          },
          onError: (error) => {
            toastr.error(error.message);
          },
        },
      );
    },
  });

  return (
    <Card
      footer={
        <CardFooter px={1} py={1}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            width="100%"
            gap={2}
          >
            <Button
              onClick={() => {
                void form.handleSubmit();
              }}
              disabled={form.state.isSubmitting}
            >
              {form.state.isSubmitting ? (
                <Loader
                  loaderType="ring"
                  variant="inline"
                  loaderLabel="Saving..."
                  size="small"
                />
              ) : (
                'Save changes to company'
              )}
            </Button>
          </Box>
        </CardFooter>
      }
    >
      <CardRow pb={0}>
        <CardColumn align="left">
          <Typography variant="h1" mt={2}>
            Update company details
          </Typography>
        </CardColumn>
      </CardRow>
      <Divider type="horizontal" mt={2} mb={2} />
      <Form errorCount={form.state.errors.length}>
        <Textbox
          value={company.externalId}
          label="Company ID"
          readOnly
          onChange={noop}
        />
        <Textbox label="SIT ID" value={company.id} readOnly onChange={noop} />
        <Textbox
          label="Admin username"
          value={company.adminUsername ?? ''}
          readOnly
          onChange={noop}
        />
        <Textbox
          label="Admin email"
          value={company.adminEmail ?? ''}
          readOnly
          onChange={noop}
        />
        <Textbox
          label="Last login attempt"
          value={company.lastLoginAttempt ?? ''}
          readOnly
          onChange={noop}
        />
        <Textbox
          label="Sync start"
          value={company.syncStart ?? ''}
          readOnly
          onChange={noop}
        />
        <Textbox
          label="Sync end"
          value={company.syncEnd ?? ''}
          readOnly
          onChange={noop}
        />
        <Box display="flex">
          <Box flexGrow={1}>
            <form.Field
              name="syncError"
              validators={{
                onChange: ({ value }) => {
                  return value === '' ? undefined : undefined;
                },
              }}
            >
              {(field) => (
                <Textbox
                  name={field.name}
                  value={field.state.value ?? ''}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  label="Sync error"
                  readOnly
                />
              )}
            </form.Field>
          </Box>
          <Box mt="25px">
            <Button
              variantType="secondary"
              onClick={() => {
                form.setFieldValue('syncError', '');
              }}
            >
              Clear error
            </Button>
          </Box>
        </Box>
        <form.Field name="customIntacctEndpoint">
          {(field) => (
            <Textbox
              name={field.name}
              value={field.state.value ?? ''}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              label="Custom Intacct endpoint"
            />
          )}
        </form.Field>
        <form.Field
          name="tenantType"
          validators={{
            onChange: ({ value }) => {
              return !value ? 'This field is required' : undefined;
            },
          }}
        >
          {(field) => (
            <Select
              name={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => {
                if (isTenantType(e.target.value)) {
                  field.handleChange(e.target.value);
                }
              }}
              label="Tenant type"
              required
            >
              <Option value="customer_production" text="Customer Production" />
              <Option value="partner_production" text="Partner Production" />
              <Option value="SIAP_production" text="SIAP Production" />
              <Option value="MEDC_production" text="MEDC Production" />
              <Option value="sandbox" text="Sandbox" />
              <Option value="developer" text="Developer" />
              <Option value="sales_demo" text="Sales Demo" />
              <Option value="implementation" text="Implementation" />
              <Option value="debug" text="Debug" />
              <Option value="test" text="Test" />
              <Option value="preview" text="Preview" />
              <Option value="other" text="Other" />
            </Select>
          )}
        </form.Field>
      </Form>
    </Card>
  );
}
